import type { ChatDialogResponse, ChatRealtimeEventResponse } from '@/core/generated/api';
import { matchesChatDialogSearch, matchesChatDialogTab } from './filters';
import { RequestStatus } from '@/shared/model/requestStatus';
import { markAcknowledgedChatDialogMessagesRead, mergeChatDialogMessages } from './selectors';
import type { ChatDialogData, ChatDialogRealtimeVersions } from './types';

/** Сортирует диалоги по последней активности и идентификатору */
const compareDialogs = (left: ChatDialogResponse, right: ChatDialogResponse): number => {
    const byDate = (Date.parse(right.lastActivityAt ?? '') || 0)
        - (Date.parse(left.lastActivityAt ?? '') || 0);

    if (Number.isFinite(byDate) && byDate !== 0) return byDate;
    return (right.id ?? '').localeCompare(left.id ?? '');
};

/** Применяет изменение переписки и запоминает версии показанных данных */
export const applyChatDialogRealtimeEvent = (
    state: ChatDialogData,
    event: ChatRealtimeEventResponse,
    versions: ChatDialogRealtimeVersions,
): Partial<ChatDialogData> => {
    const revision = BigInt(event.revision);
    const dialogId = event.dialogId;

    if (!dialogId || revision <= versions.baseline) return {};
    const patch: Partial<ChatDialogData> = {};

    if (revision >= versions.counters) {
        versions.counters = revision;
        patch.counters = event.counters;
        patch.countersStatus = RequestStatus.Success;
        patch.countersError = null;
    }

    const currentDialog = revision >= (versions.dialogs.get(dialogId) ?? versions.baseline);

    if (currentDialog) {
        versions.dialogs.set(dialogId, revision);

        const previous = state.selectedDialog?.id === dialogId
            ? state.selectedDialog
            : state.dialogs.find((item) => item.id === dialogId);

        const sameCompanion = previous?.companionId === event.dialog?.companionId
            && previous?.companionAppUserId === event.dialog?.companionAppUserId;

        const dialog = event.dialog && {
            ...event.dialog,
            companionPetNames: event.dialog.companionPetNames
                ?? (sameCompanion
                    ? previous?.companionPetNames
                    : undefined),
        };

        const visible = dialog && matchesChatDialogSearch(state, dialog)
            && matchesChatDialogTab(state.filter, dialog);

        const others = state.dialogs.filter((item) => item.id !== dialogId);

        const dialogs = visible
            ? [...others, dialog].sort(compareDialogs)
            : others;

        const capacity = state.nextOffset ?? dialogs.length;
        patch.dialogs = dialogs.slice(0, capacity);
        patch.nextOffset = state.nextOffset === null
            ? null
            : patch.dialogs.length;

        if (state.selectedDialog?.id === dialogId) {
            patch.selectedDialog = dialog ?? null;
            patch.lastReadSequence = Math.max(state.lastReadSequence, event.readThroughSequence ?? 0);

            if (!dialog) {
                patch.messages = [];
                patch.nextBeforeSequence = null;
                patch.nextAfterSequence = null;
                patch.messagesStatus = RequestStatus.Idle;
                patch.historyStatus = RequestStatus.Idle;
                patch.readStatus = RequestStatus.Idle;
                patch.lastReadSequence = 0;
            }
        }
    }

    if (state.selectedDialog?.id !== dialogId || patch.selectedDialog === null) return patch;
    let messages = state.messages;
    const messageId = event.message?.id ?? event.removedMessageId;

    const messageRevision = messageId
        ? versions.messages.get(messageId) ?? versions.baseline
        : revision;

    if (messageId && revision >= messageRevision) {
        versions.messages.set(messageId, revision);

        if (event.message) {
            const oldestSequence = state.messages[0]?.sequence ?? 0;

            const shown = state.messages.some((item) => item.id === messageId)
                || (state.nextAfterSequence === null && (event.message.sequence ?? 0) >= oldestSequence);

            if (shown) messages = mergeChatDialogMessages(messages, [event.message]);
        } else {
            messages = messages.filter((item) => item.id !== messageId);
        }
    }

    patch.messages = markAcknowledgedChatDialogMessagesRead(
        messages,
        patch.lastReadSequence ?? state.lastReadSequence,
    );

    return patch;
};
